// TimeScrubber.tsx — Pattern 2 (TIMELINE SCRUBBING) UI for the v4 shell
// (M-PWA-OPERATOR-UX-V2 slice 2).
//
// A bottom sheet opened via window.__openScrubber. The operator scrubs to a
// past moment with preset ranges (now / -1h / -6h / -1d / -1w) or a slider
// over the last 7 days, then applies it via useTimeShift().setAsOf — the shell
// re-renders the active screen keyed by asOf and data fetches pass ?as_of=.
// "Now" / Go live sets asOf back to null (= live).

import { useMemo, useState } from "react";
import { useTimeShift } from "./TimeShift.js";

const HOUR_MS = 3_600_000;
/** Scrub range: the last 7 days, in hours. */
const MAX_HOURS_BACK = 168;

const PRESETS: { label: string; hoursBack: number }[] = [
  { label: "Now", hoursBack: 0 },
  { label: "-1h", hoursBack: 1 },
  { label: "-6h", hoursBack: 6 },
  { label: "-1d", hoursBack: 24 },
  { label: "-1w", hoursBack: 168 },
];

function formatMoment(d: Date): string {
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Hours between now and an ISO timestamp, clamped to the scrub range. */
function hoursBackFrom(asOf: string | null): number {
  if (!asOf) return 0;
  const diff = Date.now() - Date.parse(asOf);
  if (Number.isNaN(diff) || diff <= 0) return 0;
  return Math.min(MAX_HOURS_BACK, Math.round(diff / HOUR_MS));
}

export function TimeScrubber({ onClose }: { onClose: () => void }) {
  const { asOf, setAsOf, isLive } = useTimeShift();
  // Pending scrub position (hours back from now); 0 = live. Seeded from the
  // current asOf so reopening the sheet shows where the operator already is.
  const [hoursBack, setHoursBack] = useState<number>(() => hoursBackFrom(asOf));

  // Anchor "now" once per open so the slider target doesn't drift per render.
  const openedAt = useMemo(() => Date.now(), []);
  const target = new Date(openedAt - hoursBack * HOUR_MS);
  const pendingLive = hoursBack === 0;

  const apply = () => {
    setAsOf(pendingLive ? null : target.toISOString());
    onClose();
  };

  const goLive = () => {
    setAsOf(null);
    onClose();
  };

  return (
    <>
      <div
        className="sheet-backdrop open"
        style={{ position: "fixed" }}
        onClick={onClose}
      />
      <div className="sheet open" style={{ position: "fixed" }} role="dialog" aria-label="Timeline scrubber">
        <div className="sheet-handle" />
        <div className="sheet-head">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div className="eyebrow">Timeline · view as of</div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                color: "var(--text-dim)",
                fontSize: 16,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
          <h3 style={{ fontSize: 15, lineHeight: 1.35, marginTop: 6 }}>
            {pendingLive ? "Live — following now" : formatMoment(target)}
          </h3>
          <span className={`chip ${pendingLive ? "ok" : "warn"}`} style={{ fontSize: 10 }}>
            {pendingLive ? "● LIVE" : `◷ ${hoursBack}h ago`}
          </span>
        </div>

        <div className="sheet-body">
          <div className="sheet-section" style={{ marginTop: 0 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  className={hoursBack === p.hoursBack ? "chip accent" : "chip"}
                  style={{ cursor: "pointer" }}
                  onClick={() => setHoursBack(p.hoursBack)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="sheet-section">
            {/* Slider runs left = -7d ... right = now, so dragging right moves toward live. */}
            <input
              type="range"
              min={0}
              max={MAX_HOURS_BACK}
              step={1}
              value={MAX_HOURS_BACK - hoursBack}
              onChange={(e) => setHoursBack(MAX_HOURS_BACK - Number(e.target.value))}
              aria-label="Scrub timeline (last 7 days)"
              style={{ width: "100%" }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 10,
                color: "var(--text-dim)",
                fontFamily: "var(--font-mono)",
                marginTop: 4,
              }}
            >
              <span>-7d</span>
              <span>-1d</span>
              <span>now</span>
            </div>
          </div>

          <div className="sheet-section" style={{ display: "flex", gap: 8 }}>
            <button
              className="chip"
              style={{ cursor: "pointer", flex: 1, padding: "10px 12px", textAlign: "center" }}
              onClick={goLive}
              disabled={isLive && pendingLive}
            >
              ● Go live
            </button>
            <button
              className="chip accent"
              style={{ cursor: "pointer", flex: 2, padding: "10px 12px", textAlign: "center" }}
              onClick={apply}
            >
              {pendingLive ? "Follow live" : `View as of ${formatMoment(target)}`}
            </button>
          </div>

          <p style={{ fontSize: 11, color: "var(--text-dim)", lineHeight: 1.5, padding: "0 2px 12px" }}>
            Scrubbed views reconstruct entity state from the temporal archive.
            Panels without history (attention queue, plan health) keep showing
            live state and are marked LIVE.
          </p>
        </div>
      </div>
    </>
  );
}
